import axios, { AxiosInstance } from 'axios';
import { toast } from 'react-toastify';
import msalInstance from '../auth/msalInstance';
import { getAccessTokenSilently, apiRequest } from '../auth/authConfig';
import errorHandler from '@/services/errorHandler';

// Shared in-flight refresh promise so concurrent 401s only trigger a single
// token refresh instead of N parallel forceRefresh calls.
let refreshPromise: Promise<string | null> | null = null;

const getSharedRefreshedToken = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = getAccessTokenSilently(msalInstance, {
      ...apiRequest,
      forceRefresh: true,
    }).finally(() => {
      // Clear once settled so a future 401 can refresh again.
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Clear stale auth state and send the user to /login. Guards against redirect
// loops by not redirecting when we're already on the login page.
const handleUnrecoverableAuth = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem('auth_token');
  } catch {
    // Ignore storage access errors (e.g. private mode).
  }
  delete axiosInstance.defaults.headers.common['Authorization'];

  try {
    toast.error('Your session has expired. Please sign in again.', {
      toastId: 'session-expired',
    });
  } catch {
    // Toast is best-effort; never block the redirect on it.
  }

  if (window.location.pathname !== '/login') {
    window.location.href = '/login?message=session-expired';
  }
};

// Extend AxiosInstance type to include our custom methods
interface CustomAxiosInstance extends AxiosInstance {
  refreshAuthToken: () => Promise<boolean>;
}

// Create axios instance
// Use empty string as baseURL to make relative paths (will use current domain)
// This ensures API calls go through Next.js API routes, not directly to backend
const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL || '',
  timeout: 120000, // Increased to 2 minutes for large datasets
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
}) as CustomAxiosInstance;

// Define a function to get token from cookie or storage
const getAuthToken = (): string | null => {
  // Get token from localStorage or wherever you store it
  if (typeof window !== 'undefined') {
    return localStorage.getItem('auth_token');
  }
    return null;
}

// Helper function to get CSRF token from cookies
function getCsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null; // Cannot access cookies server-side
  }
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}


// Add a request interceptor to add authentication token and CSRF token
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // --- Authentication Token Handling ---
      // First try to get the Django token from localStorage
      const djangoToken = localStorage.getItem('auth_token');
      
      if (djangoToken) {
        // Use Django token authentication format
        config.headers.Authorization = `Token ${djangoToken}`;
      } else {
        // Always try MSAL authentication if no Django token
        console.debug('No Django auth token found, trying MSAL authentication');
        
        // Check if we already have a token in the Authorization header
        let currentToken = null;
        if (config.headers?.Authorization) {
          const authHeader = config.headers.Authorization as string;
          if (authHeader.startsWith('Bearer ')) {
            currentToken = authHeader.substring(7);
            
            // Check if the token is expired or about to expire
            if (currentToken && isTokenExpired(currentToken)) {
              if (process.env.NODE_ENV !== 'production') {
                console.log('Token is expired or about to expire, refreshing...');
              }
              await axiosInstance.refreshAuthToken();
              // The refreshAuthToken updates the default headers, so we don't need to do anything else
              return config;
            }
            
            // If we have a valid token already, just use it
            return config;
          }
        }
        
        // No valid token in headers, try to get a new one
        const token = await getAccessTokenSilently(msalInstance, apiRequest);
        
        // If we have a token, add it to the request
        if (token) {
          // Use Bearer format for MSAL tokens
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('No token available for request. Will proceed without authentication header.');
        }
      }
      
      // Log request info for debugging (only in development)
      if (process.env.NODE_ENV !== 'production') {
        console.debug(`Request: ${config.method?.toUpperCase()} ${config.url}`);
      }
    } catch (error) {
      console.error('Error getting token for request:', error);
      // Continue with request without token, but still add CSRF token if applicable
    }

    // --- CSRF Token Handling ---
    // Add CSRF token for unsafe methods (POST, PUT, PATCH, DELETE)
    const method = config.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        // CSRF token is optional for API requests with proper authentication
        // Django REST framework will use authentication header instead
        console.debug('CSRF token not found, relying on authentication header');
      }
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Network errors (and request setup errors) have no config / response.
    // Guard before reading originalRequest._retry to avoid a crash.
    if (!error.config) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // If error is 401 (Unauthorized) and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Received 401, attempting to get a new token...');
      }
      originalRequest._retry = true;

      // Django-token requests have no refresh story: a 401 here is terminal.
      // Treat them as unrecoverable straight away so the user isn't stranded.
      const authHeader = originalRequest.headers?.Authorization as string | undefined;
      const isDjangoTokenRequest =
        typeof authHeader === 'string' && authHeader.startsWith('Token ');

      if (!isDjangoTokenRequest) {
        try {
          // Single shared refresh shared across all concurrent 401s.
          const token = await getSharedRefreshedToken();

          if (token) {
            // Update both the default and the retried request headers.
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            originalRequest.headers.Authorization = `Bearer ${token}`;
            // Retry the request
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Use error handler to log
          errorHandler.handle(refreshError, 'Authentication', {
            showToast: false,
            category: 'AUTH' as any
          });
        }
      }

      // Refresh was impossible or failed: clear stale auth and redirect to /login.
      handleUnrecoverableAuth();
      return Promise.reject(error);
    }

    // Handle other errors with the error handler
    const endpoint = originalRequest?.url || 'unknown';
    errorHandler.handle(error, `API: ${endpoint}`, { showToast: false });

    return Promise.reject(error);
  }
);

export default axiosInstance;

/**
 * Fetches CSV data for impressions or clicks for a specific client.
 * @param clientId The client ID (short name) like 'uea', not the full name
 * @param dataType The type of data to fetch ('impressions' or 'clicks')
 * @returns The CSV content as a string
 */
export const fetchCSVData = async (clientId: string, dataType: 'impressions' | 'clicks' | 'conversions') => {
  const fileName = `${dataType}-data.csv`;
  // Default to client-specific directory, but override for impressions/clicks
  let targetDir = clientId.toLowerCase(); 
  
  // Force 'default' directory for impressions and clicks data
  if (dataType === 'impressions' || dataType === 'clicks' || dataType === 'conversions') {
    targetDir = 'default';
    console.log(`Forcing 'default' directory for ${dataType} data.`);
  } else {
     console.log(`Using client directory '${targetDir}' for ${dataType} data.`);
  }

  // Construct paths using the determined target directory (deduped).
  const possiblePaths = [
    `/data/clients/${targetDir}/${fileName}`,    // Standard path
    `/clients/${targetDir}/${fileName}`,         // Without /data prefix
  ];

  console.log(`Attempting to fetch ${dataType} data using target directory: ${targetDir}`);

  // Try each path until one works
  for (const path of possiblePaths) {
    try {
      console.log(`Trying path: ${path}`);
      const response = await axios.get(path);
      console.log(`Successfully fetched ${dataType} data from: ${path}`);
      return response.data;
    } catch (error) {
      console.log(`Failed to fetch from ${path}`);
      // Continue to the next path
    }
  }

  // If all paths fail, return null so callers can distinguish failure from an
  // empty-but-valid dataset (previously a header-only CSV masked failures).
  console.error(`Error fetching ${dataType} data (targetDir: ${targetDir}): all known paths failed`);
  return null;
};

// Utility function to test authentication with Django
export const testDjangoAuth = async (): Promise<{success: boolean, message: string}> => {
  try {
    // First get a token directly
    if (!msalInstance) {
      return { success: false, message: "MSAL instance not available" };
    }
    
    const token = await getAccessTokenSilently(msalInstance, apiRequest);
    if (!token) {
      return { success: false, message: "Failed to acquire token" };
    }
    
    // Test with a simple, non-destructive API call
    try {
      // First try with our automatic axiosInstance
      const response = await axiosInstance.get('/api/v1/auth-test/');
      return {
        success: true,
        message: "Successfully authenticated with axiosInstance"
      };
    } catch (autoError: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Auto auth test failed:", autoError);
      }

      // If that fails, try manual token application
      const manualResponse = await axios.get(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/v1/auth-test/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        message: "Successfully authenticated with manual token application"
      };
    }
  } catch (error: any) {
    return { 
      success: false, 
      message: `Authentication test failed: ${error.message}`,
    };
  }
};

// Utility function to check if a token is expired (or about to expire in the next minute)
const isTokenExpired = (token: string): boolean => {
  try {
    // Split the token to get the payload
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.warn('Token does not have expected parts');
      return true; // Assume expired if token doesn't look right
    }
    
    // Decode the payload (second part)
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );
    
    const payload = JSON.parse(jsonPayload);
    
    // Check exp claim
    if (!payload.exp) {
      console.warn('Token does not have expiration time');
      return true; // Assume expired if no exp claim
    }
    
    // Get expiration time in milliseconds
    const expirationTime = payload.exp * 1000;
    // Check if token expires in the next minute
    return Date.now() >= expirationTime - 60000;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume expired on error
  }
};

// Add a method to axiosInstance to handle token refresh
axiosInstance.refreshAuthToken = async (): Promise<boolean> => {
  try {
    // Force a new token acquisition
    const token = await getAccessTokenSilently(msalInstance, {
      ...apiRequest,
      forceRefresh: true
    });
    
    if (token) {
      // Update the default headers for future requests with Bearer format
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      if (process.env.NODE_ENV !== 'production') {
        console.log('Successfully refreshed auth token');
      }
      return true;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to refresh auth token');
    }
    return false;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error refreshing auth token:', error);
    }
    return false;
  }
};